import React, { useCallback, useEffect, useRef, useState } from 'react';
import '../../../../css/common/chat/ChatTargetSwitchFab.css';

const clampBadge = (value) => (Number(value || 0) > 99 ? '99+' : String(Number(value || 0)));
const REVEAL_DELAY_MS = 500;
const IDLE_HIDE_MS = 3000;

export default function ChatTargetSwitchFab({
  label,
  iconClassName,
  unreadCount = 0,
  badgeToneClassName = 'sidebar-count-badge--alert',
  onClick,
  secondaryAction = null,
  className = '',
}) {
  const count = Number(unreadCount || 0);
  const showBadge = count > 0;
  const hasSecondaryAction = Boolean(secondaryAction?.onClick);
  const [secondaryOpen, setSecondaryOpen] = useState(false);
  const revealTimerRef = useRef(null);
  const hideTimerRef = useRef(null);
  const suppressNextClickRef = useRef(false);

  const clearRevealTimer = useCallback(() => {
    if (revealTimerRef.current) {
      window.clearTimeout(revealTimerRef.current);
      revealTimerRef.current = null;
    }
  }, []);

  const clearHideTimer = useCallback(() => {
    if (hideTimerRef.current) {
      window.clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const hideSecondary = useCallback(() => {
    clearRevealTimer();
    clearHideTimer();
    setSecondaryOpen(false);
  }, [clearHideTimer, clearRevealTimer]);

  const scheduleHideSecondary = useCallback(() => {
    if (!hasSecondaryAction) return;
    clearHideTimer();
    hideTimerRef.current = window.setTimeout(() => {
      setSecondaryOpen(false);
      hideTimerRef.current = null;
    }, IDLE_HIDE_MS);
  }, [clearHideTimer, hasSecondaryAction]);

  const revealSecondary = useCallback(() => {
    if (!hasSecondaryAction) return;
    clearRevealTimer();
    clearHideTimer();
    setSecondaryOpen(true);
    scheduleHideSecondary();
  }, [clearHideTimer, clearRevealTimer, hasSecondaryAction, scheduleHideSecondary]);

  const startRevealDelay = useCallback(() => {
    if (!hasSecondaryAction) return;
    clearRevealTimer();
    clearHideTimer();
    revealTimerRef.current = window.setTimeout(() => {
      revealSecondary();
      revealTimerRef.current = null;
    }, REVEAL_DELAY_MS);
  }, [clearHideTimer, clearRevealTimer, hasSecondaryAction, revealSecondary]);

  const startTouchRevealDelay = useCallback(() => {
    if (!hasSecondaryAction) return;
    clearRevealTimer();
    clearHideTimer();
    suppressNextClickRef.current = false;
    revealTimerRef.current = window.setTimeout(() => {
      suppressNextClickRef.current = true;
      revealSecondary();
      revealTimerRef.current = null;
    }, REVEAL_DELAY_MS);
  }, [clearHideTimer, clearRevealTimer, hasSecondaryAction, revealSecondary]);

  useEffect(() => {
    return () => {
      clearRevealTimer();
      clearHideTimer();
    };
  }, [clearHideTimer, clearRevealTimer]);

  const handleMainClick = (event) => {
    if (suppressNextClickRef.current) {
      event.preventDefault();
      event.stopPropagation();
      suppressNextClickRef.current = false;
      return;
    }
    onClick?.(event);
  };

  const handleTouchStart = () => {
    if (!hasSecondaryAction) return;
    startTouchRevealDelay();
  };

  const handleTouchEnd = () => {
    clearRevealTimer();
    if (secondaryOpen) scheduleHideSecondary();
  };

  const handleSecondaryClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    hideSecondary();
    secondaryAction?.onClick?.(event);
  };

  return (
    <div
      className={`chat-target-switch-fab-stack${secondaryOpen ? ' is-action-open' : ''} ${className}`.trim()}
      onMouseEnter={startRevealDelay}
      onMouseLeave={() => {
        clearRevealTimer();
        if (secondaryOpen) scheduleHideSecondary();
      }}
    >
      {hasSecondaryAction ? (
        <button
          type="button"
          className="chat-target-switch-fab__secondary"
          onClick={handleSecondaryClick}
          onMouseEnter={() => {
            clearRevealTimer();
            clearHideTimer();
          }}
          onMouseLeave={scheduleHideSecondary}
          aria-label={secondaryAction.label}
          aria-hidden={!secondaryOpen}
          title={secondaryAction.label}
          tabIndex={secondaryOpen ? 0 : -1}
        >
          <i className={`chat-target-switch-fab__secondaryIcon ${secondaryAction.iconClassName || 'fa-solid fa-plus'}`} aria-hidden="true" />
        </button>
      ) : null}
      <button
        type="button"
        className="chat-target-switch-fab"
        onClick={handleMainClick}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
        aria-label={label}
        aria-expanded={hasSecondaryAction ? secondaryOpen : undefined}
        title={label}
      >
        <i className={`chat-target-switch-fab__icon ${iconClassName}`} aria-hidden="true" />
        {showBadge ? (
          <span className={`chat-target-switch-fab__badge ${badgeToneClassName}`.trim()}>
            {clampBadge(count)}
          </span>
        ) : null}
      </button>
    </div>
  );
}
